import { EBlockType } from './enums';
import {
  ImageSource,
  POST_THUMBNAIL_SIZES,
  getCmsImageFillSource,
  getFullSizeCmsImageSrc,
} from './images/cmsImage';
import {
  BlockType,
  IBlock,
  IBlockItem,
  ICtaItem,
  IDailyBonusItem,
  IMediaItem,
  IPostItem,
} from './types';

/**
 * Every image a block will render, as the exact source each component requests
 * (optimizer URL, srcset, sizes), so preloading them warms the right cache entries.
 */
export const getBlockImageSources = (block: IBlock): ImageSource[] => {
  const sources = [block.backgroundImage, block.backgroundImageMobile].map(asIs);

  block.items?.forEach((item: IBlockItem) => {
    sources.push(...getItemImageSources(item, block));
  });

  return dedupe(sources);
};

export const getBatchImageSources = (blocks: IBlock[]): ImageSource[] => {
  return dedupe(blocks.flatMap(getBlockImageSources));
};

const getItemImageSources = (item: IBlockItem, block: IBlock): (ImageSource | undefined)[] => {
  const itemType: BlockType = item.type || block.contentType || block.type;

  switch (itemType) {
    case EBlockType.ARTICLE:
    case EBlockType.NEWS:
    case EBlockType.CONTENTS_PREVIEW:
      // Mirrors <CmsImage fill sizes={POST_THUMBNAIL_SIZES}> in PostItem.
      return [asPostThumbnail((item as IPostItem).thumbnailImage)];
    case EBlockType.IMAGE:
      // Mirrors ImageItem: same pixel size, WebP.
      return [(item as IMediaItem).url, (item as IMediaItem).mobileUrl].map(asFullSize);
    case EBlockType.CTA:
      return [(item as ICtaItem).assetUrl, (item as ICtaItem).assetUrlMobile].map(asFullSize);
    case EBlockType.DAILY_BONUS:
      return [asIs(getDailyBonusProductImage(item as IDailyBonusItem))];
    default:
      return [];
  }
};

const asIs = (url: string | undefined): ImageSource | undefined => (url ? { src: url } : undefined);

const asFullSize = (url: string | undefined): ImageSource | undefined =>
  url ? { src: getFullSizeCmsImageSrc(url) } : undefined;

const asPostThumbnail = (url: string | undefined): ImageSource | undefined =>
  url ? getCmsImageFillSource(url, POST_THUMBNAIL_SIZES) : undefined;

/** Mirrors the product `DailyBonusItem` selects for the current sequence index. */
const getDailyBonusProductImage = (item: IDailyBonusItem): string | undefined => {
  const currentProductIndex = item.indexToCollect ? item.indexToCollect - 1 : 0;

  return item.productsSequence?.[currentProductIndex]?.products?.[0]?.images?.product;
};

const dedupe = (sources: (ImageSource | undefined)[]): ImageSource[] => {
  const bySrc = new Map<string, ImageSource>();

  for (const source of sources) {
    if (source && !bySrc.has(source.src)) bySrc.set(source.src, source);
  }

  return Array.from(bySrc.values());
};
